import { motion } from "framer-motion";
import { ShoppingBag, SearchX, WifiOff, Clock, AlertTriangle, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { restaurant } from "@/data/restaurant";

function Shell({ icon, title, desc, children, testId }) {
  return (
    <motion.div
      className="flex min-h-[55vh] flex-col items-center justify-center px-8 text-center"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={testId}
    >
      <div className="mb-5 grid h-20 w-20 place-items-center rounded-3xl bg-secondary text-muted-foreground">
        {icon}
      </div>
      <h3 className="mb-1.5 font-heading text-xl font-bold">{title}</h3>
      <p className="mb-6 max-w-xs text-sm text-muted-foreground">{desc}</p>
      {children}
    </motion.div>
  );
}

export function EmptyCart({ onBrowse }) {
  const { t } = useLang();
  return (
    <Shell
      testId="empty-cart-state"
      icon={<ShoppingBag className="h-9 w-9" />}
      title={t("emptyCart")}
      desc={t("emptyCartDesc")}
    >
      <Button onClick={onBrowse} className="rounded-full px-6" data-testid="empty-cart-browse-btn">
        {t("browseMenu")}
      </Button>
    </Shell>
  );
}

export function NoResults() {
  const { t } = useLang();
  return (
    <Shell
      testId="no-results-state"
      icon={<SearchX className="h-9 w-9" />}
      title={t("noResults")}
      desc={t("noResultsDesc")}
    />
  );
}

export function NoActiveOrder({ onBrowse }) {
  const { t } = useLang();
  return (
    <Shell
      testId="no-order-state"
      icon={<Receipt className="h-9 w-9" />}
      title={t("noActiveOrder")}
      desc={t("noActiveOrderDesc")}
    >
      <Button onClick={onBrowse} className="rounded-full px-6" data-testid="no-order-browse-btn">
        {t("browseMenu")}
      </Button>
    </Shell>
  );
}

export function OfflineState({ onRetry }) {
  const { t } = useLang();
  return (
    <Shell
      testId="offline-state"
      icon={<WifiOff className="h-9 w-9" />}
      title={t("offlineTitle")}
      desc={t("offlineDesc")}
    >
      <Button onClick={onRetry} className="rounded-full px-6" data-testid="offline-retry-btn">
        {t("retry")}
      </Button>
    </Shell>
  );
}

// Card has no menu (unknown/inactive card, no restaurant binding). No retry:
// the guest needs the staff, and no other restaurant's menu is ever shown.
export function MenuUnavailableState() {
  const { t } = useLang();
  return (
    <Shell
      testId="menu-unavailable-state"
      icon={<AlertTriangle className="h-9 w-9" />}
      title={t("menuUnavailableTitle")}
      desc={t("menuUnavailableDesc")}
    />
  );
}

export function ErrorState({ onRetry }) {
  const { t } = useLang();
  return (
    <Shell
      testId="error-state"
      icon={<AlertTriangle className="h-9 w-9" />}
      title={t("errorTitle")}
      desc={t("errorDesc")}
    >
      <Button onClick={onRetry} className="rounded-full px-6" data-testid="error-retry-btn">
        {t("retry")}
      </Button>
    </Shell>
  );
}

export function ClosedState() {
  const { t } = useLang();
  return (
    <Shell
      testId="closed-state"
      icon={<Clock className="h-9 w-9" />}
      title={t("closedTitle")}
      desc={`${t("closedDesc")} ${restaurant.hours}`}
    />
  );
}
